// Luồng giao việc: ai được làm gì ở trạng thái nào. Thuần hàm để dễ test và dùng lại phía API.
//
//  Tạo ──► Chưa bắt đầu ──(người làm: Bắt đầu)──► Đang làm ──(người làm: Báo hoàn thành)──┬─ cần duyệt ─► Chờ duyệt ──(người giao: Duyệt)──► Hoàn thành
//                                                    ▲                                     │                 │
//                                                    └──────────(người giao: Trả lại + lý do)◄───────────────┘
//                                                                                          └─ không cần duyệt ─────────────────────────────► Hoàn thành
//  Người giao / quản lý: giao lại người làm, đổi hạn (ghi lý do), đóng "Không đạt", huỷ, mở lại việc đã đóng.
import dayjs from 'dayjs';
import { memberOf, type Repeat, type TaskActivity, type TaskStatus, type WorkTask } from './data';

export const STATUS_META: Record<TaskStatus, { label: string; color: string; tag: string }> = {
  todo: { label: 'Chưa bắt đầu', color: '#94a3b8', tag: 'default' },
  doing: { label: 'Đang làm', color: '#3b82f6', tag: 'processing' },
  review: { label: 'Chờ duyệt', color: '#a855f7', tag: 'purple' },
  done: { label: 'Hoàn thành', color: '#22c55e', tag: 'success' },
  failed: { label: 'Không đạt', color: '#64748b', tag: 'default' },
  cancelled: { label: 'Đã huỷ', color: '#cbd5e1', tag: 'default' },
};

export const ymd = (d?: dayjs.ConfigType) => dayjs(d).format('YYYY-MM-DD');

export const isClosed = (t: WorkTask) => t.status === 'done' || t.status === 'failed' || t.status === 'cancelled';
export const isOverdue = (t: WorkTask, now = ymd()) => !isClosed(t) && t.status !== 'review' && !!t.dueAt && t.dueAt < now;
export const daysLate = (t: WorkTask, now = ymd()) => (t.dueAt ? dayjs(now).diff(t.dueAt, 'day') : 0);

export const isAssignee = (t: WorkTask, me: string) => t.assigneeId === me;
/** Người giao việc, trưởng phòng của việc đó hoặc quản lý toàn công ty được điều chỉnh / duyệt */
export const canManage = (t: WorkTask, me: string) => {
  const m = memberOf(me);
  return t.creatorId === me || m?.manager === 'all' || (m?.manager === 'department' && m.departmentId === t.departmentId);
};

export type Action = 'start' | 'submit' | 'approve' | 'return' | 'reassign' | 'due' | 'fail' | 'cancel' | 'reopen' | 'edit';

export const ACTION_LABEL: Record<Action, string> = {
  start: 'Bắt đầu làm',
  submit: 'Báo hoàn thành',
  approve: 'Duyệt hoàn thành',
  return: 'Trả lại làm tiếp',
  reassign: 'Giao lại',
  due: 'Đổi hạn chót',
  fail: 'Đóng – không đạt',
  cancel: 'Huỷ việc',
  reopen: 'Mở lại',
  edit: 'Sửa',
};

/** Các thao tác người dùng hiện tại được làm với việc này */
export function allowedActions(t: WorkTask, me: string): Action[] {
  const a: Action[] = [];
  const mine = isAssignee(t, me);
  const mgr = canManage(t, me);
  if (t.status === 'todo' && mine) a.push('start');
  if ((t.status === 'todo' || t.status === 'doing') && mine) a.push('submit');
  // người làm không tự duyệt việc của mình (trừ việc tự giao cho bản thân)
  if (t.status === 'review' && mgr && (!mine || t.creatorId === me)) a.push('approve', 'return');
  if (!isClosed(t) && mgr) a.push('reassign', 'due', 'edit', 'fail', 'cancel');
  if (isClosed(t) && mgr) a.push('reopen');
  return a;
}

/** Người làm hoặc người quản lý việc được tích checklist khi việc còn mở */
export const canCheck = (t: WorkTask, me: string) => !isClosed(t) && t.status !== 'review' && (isAssignee(t, me) || canManage(t, me));

export const checklistLeft = (t: WorkTask) => (t.checklist ?? []).filter(c => !c.done).length;

/** Lý do chặn báo hoàn thành (còn việc con chưa xong, checklist quy trình chưa tích đủ) */
export function submitBlocker(t: WorkTask, all: WorkTask[]): string | null {
  const open = all.filter(s => s.parentId === t.id && !isClosed(s));
  if (open.length) return `Còn ${open.length} việc con chưa xong`;
  const left = checklistLeft(t);
  return left ? `Còn ${left} mục checklist chưa làm` : null;
}

const act = (by: string, type: TaskActivity['type'], text?: string, photos?: string[]): TaskActivity => ({
  id: `act-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`, at: new Date().toISOString(), by, type, text, ...(photos?.length ? { photos } : {}),
});

export interface TransitionInput { by: string; note?: string; assigneeId?: string; dueAt?: string; now?: string; photos?: string[] }

/** Áp một thao tác, trả về việc đã cập nhật hoặc thông báo lỗi */
export function applyAction(t: WorkTask, action: Action, input: TransitionInput): WorkTask | string {
  const now = input.now ?? ymd();
  if (!allowedActions(t, input.by).includes(action)) return 'Bạn không có quyền thực hiện thao tác này';
  const log = (type: TaskActivity['type'], text?: string, photos?: string[]) => [...t.activity, act(input.by, type, text, photos)];
  switch (action) {
    case 'start':
      return { ...t, status: 'doing', startAt: t.startAt ?? now, activity: log('start') };
    case 'submit':
      if (checklistLeft(t)) return `Còn ${checklistLeft(t)} mục checklist chưa làm`;
      if (t.needProof && !input.photos?.length) return 'Việc này cần đính kèm ảnh kết quả khi báo hoàn thành';
      return t.needReview
        ? { ...t, status: 'review', submittedAt: now, activity: log('submit', input.note, input.photos) }
        : { ...t, status: 'done', submittedAt: now, doneAt: now, activity: log('submit', input.note, input.photos) };
    case 'approve':
      // tính đúng hạn theo ngày người làm báo xong, không phụ thuộc người duyệt chậm
      return { ...t, status: 'done', doneAt: t.submittedAt ?? now, activity: log('approve', input.note) };
    case 'return':
      if (!input.note?.trim()) return 'Cần ghi lý do trả lại';
      return { ...t, status: 'doing', submittedAt: undefined, activity: log('return', input.note) };
    case 'reassign':
      if (!input.assigneeId) return 'Chọn người làm';
      if (input.assigneeId === t.assigneeId) return 'Người làm không thay đổi';
      return { ...t, assigneeId: input.assigneeId, activity: log('assign', input.assigneeId) };
    case 'due':
      if (!input.dueAt) return 'Chọn hạn chót mới';
      if (!input.note?.trim()) return 'Cần ghi lý do đổi hạn';
      return { ...t, dueAt: input.dueAt, activity: log('due', `${t.dueAt ? dayjs(t.dueAt).format('DD/MM/YYYY') : 'Không hạn'} → ${dayjs(input.dueAt).format('DD/MM/YYYY')}: ${input.note}`) };
    case 'fail':
      if (!input.note?.trim()) return 'Cần ghi lý do';
      return { ...t, status: 'failed', activity: log('fail', input.note) };
    case 'cancel':
      if (!input.note?.trim()) return 'Cần ghi lý do huỷ';
      return { ...t, status: 'cancelled', activity: log('cancel', input.note) };
    case 'reopen':
      return { ...t, status: 'doing', doneAt: undefined, submittedAt: undefined, activity: log('reopen', input.note) };
    default:
      return t;
  }
}

const REPEAT_UNIT: Record<Repeat, 'day' | 'week' | 'month'> = { daily: 'day', weekly: 'week', monthly: 'month' };

/**
 * Lần kế tiếp của việc định kỳ khi lần này đã đóng (hoàn thành / không đạt; huỷ = dừng chuỗi).
 * Kỳ đã lỡ không tạo bù: nhảy tới kỳ gần nhất từ hôm nay. Hạn chót giữ đúng khoảng cách với ngày bắt đầu như lần trước.
 */
export function nextOccurrence(t: WorkTask, now = ymd()): Pick<WorkTask, 'startAt' | 'dueAt' | 'repeatDay'> | null {
  if (!t.repeat || t.status === 'cancelled' || !isClosed(t)) return null;
  const unit = REPEAT_UNIT[t.repeat];
  const start = dayjs(t.startAt ?? t.createdAt);
  const span = t.dueAt ? dayjs(t.dueAt).diff(start, 'day') : null;
  // Hằng tháng: giữ ngày của lần đầu trong chuỗi (31/01 → 28/02 → 31/03), không trôi dần về ngày 28
  const day = t.repeat === 'monthly' ? t.repeatDay ?? start.date() : undefined;
  const pin = (d: dayjs.Dayjs) => (day ? d.date(Math.min(day, d.daysInMonth())) : d);
  let next = pin(start.add(1, unit));
  while (next.isBefore(now, 'day')) next = pin(next.add(1, unit));
  return { startAt: ymd(next), dueAt: span === null ? undefined : ymd(next.add(span, 'day')), ...(day ? { repeatDay: day } : {}) };
}

export const ACTIVITY_TEXT: Record<TaskActivity['type'], string> = {
  create: 'tạo công việc',
  assign: 'giao cho',
  start: 'bắt đầu làm',
  submit: 'báo hoàn thành',
  approve: 'duyệt hoàn thành',
  return: 'trả lại làm tiếp',
  due: 'đổi hạn chót',
  fail: 'đóng việc – không đạt',
  cancel: 'huỷ công việc',
  reopen: 'mở lại công việc',
  comment: 'bình luận',
  edit: 'sửa thông tin',
};
